import * as React from 'react';
import { INPUT_HEIGHT } from '../../styles/global/css/GlobalInput';
import { Translate } from 'react-localize-redux';
import styled from 'styled-components';
import { FlexDiv } from '../../containers_v2/products/style';
import { DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { BorderColor } from '../../styles/global/css/Utils';
import { useFunctionState, useKeyPress } from '../../utils/customHooks';
import InputSearch from '../input/InputSearch';
import { PopupStateContext } from '../popup/PopupStateContext';
import { DropdownData, DropdownProps } from './model/Model';
import {
	ArrowBlock,
	Asterisk,
	Checkbox,
	CleanButton,
	ContainerCss,
	DropdownContainer,
	InputBlock,
	InputContainer,
	Label,
	LabelContainer,
	OptionBlock,
	OptionBlockClickable,
	OptionContainer,
	OptionImage,
	OptionImageContainer,
	OptionImageJsx,
	OptionText,
	ValidateButton,
	ValidateContainer
} from './style/Style';

const MultiLabelContainerWrapper = styled.div`
	display: flex;
	width: 100%;
	overflow: auto;
	gap: 5px;
	font-size: 12px;
	height: 100%;
	padding: 5px 10px;
`;

const MultiLabelContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${BorderColor};
	border-radius: 5px;
	padding: 5px 10px;
	height: 100%;
	white-space: nowrap;
`;

export function checkClickOut(ref: React.RefObject<HTMLDivElement | undefined>, setOpen: (value: boolean) => void) {
	React.useEffect(() => {
		function handleClickOutside(event) {
			if ((ref.current != null) && !ref.current.contains(event.target)) {
				setOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ref]);
}

function Dropdown<T = unknown, IsMulti extends boolean = false, isCheckBox extends boolean = false>(props: DropdownProps<T, IsMulti, isCheckBox>): JSX.Element {
	const { searchable = true } = props;
	type OnChangeType = IsMulti extends true ? DropdownData<T>[] : isCheckBox extends true ? DropdownData<T>[] : DropdownData<T>;
	const [list, setList] = React.useState<Array<DropdownData<T>>>(props.datalist);
	const [isOpen, setOpen] = useFunctionState<boolean>(props.isOpen ?? false, ({ newValue }) => {
		props.onOpen?.(newValue);
		return newValue;
	});
	const [label, setLabel] = React.useState<React.ReactNode>(props.defaultShowLabel ? props.defaultShowLabel : props.datalist.find(data => data.selected)?.label);
	const [value, setValue] = React.useState<DropdownData<T> | undefined>(props.datalist.find(data => data.selected));
	const [isOptionUp, setOptionUp] = React.useState<boolean>(props.dropdownStyle?.optionUp ?? false);
	const { popupState, setPopupState } = React.useContext(PopupStateContext);
	const tagId = React.useId();
	const [search, setSearch] = React.useState<string>('');

	React.useEffect(() => {
		setList(props.datalist.map((d, i) => { return { ...d, sequence: i }; }));
		if (props.selectedValue == null) {
			const newValue = props.datalist.filter(data => data.selected).shift();
			setLabel(newValue?.label);
			setValue(newValue);
		} else {
			setLabel(props.selectedValue?.label);
			setValue(props.selectedValue);
		}
	}, [JSON.stringify(props.datalist.map(data => ({ value: data.value, checked: data.checked, disabled: data.disabled })))]);

	React.useEffect(() => {
		if (props.selectedValue == null && props.cancellable) {
			setLabel('');
			setValue(undefined);
			return;
		}
		setLabel(props.selectedValue?.label);
		setValue(props.selectedValue);
	}, [props.selectedValue]);

	React.useEffect(() => {
		if (props.defaultShowLabel) {
			setLabel(props.defaultShowLabel ? props.defaultShowLabel : props.datalist.find(data => data.selected)?.label);
		} else {
			setLabel((props.selectedValue != null) ? props.selectedValue.label : props.datalist.find(data => data.selected)?.label);
		}
	}, [props.defaultShowLabel]);

	React.useEffect(() => {
		setOpen(props.isOpen ?? false);
	}, [props.isOpen]);

	React.useEffect(() => {
		const registered = popupState.includes(tagId);
		if (props.isOpen && !registered) setPopupState(state => [...state, tagId]);
		if (!(props.isOpen) && registered) setPopupState(state => state.filter(id => id !== tagId));
		return () => {
			if (popupState.includes(tagId)) setPopupState(state => state.filter(id => id !== tagId));
		};
	}, [isOpen]);

	const wrapperRef = React.useRef<HTMLDivElement>(null);

	checkClickOut(wrapperRef, setOpen);
	useKeyPress([{ key: 'Escape' }], () => {
		if (popupState[popupState.length - 1] == tagId) {
			setOpen(false);
		}
	});

	const width = props.dropdownStyle?.width ?? '200px';
	const height = props.dropdownStyle?.height ?? `${INPUT_HEIGHT}px`;
	const optionHeight = props.dropdownStyle?.optionHeight ?? '300px';
	const optionWidth = props.dropdownStyle?.optionWidth ?? '400px';
	const optionBlockHeight = props.dropdownStyle?.optionBlockHeight ?? 'auto';

	const onOptionClicked = (option: DropdownData<T>, optionI: number) => {
		if (!props.freezeShowLabel) {
			setValue(option);
			setLabel(option.label);
		}
		if (props.checkbox || props.isMulti) {
			list[optionI].checked = !option.checked;
			if (!props.validateButton) props.onChange?.(list.filter(o => o.checked) as OnChangeType);
			setList([...list]);
			return;
		}

		if (!props.checkbox && !props.isMulti) setOpen(false);
		if (!props.validateButton) props.onChange?.(option as OnChangeType);
	};

	return (
		(<div id={props.id} onClick={() => props.containerClickable} ref={wrapperRef} style={ContainerCss(props.dropdownStyle?.containerHeight ?? height, props.dropdownStyle?.containerWidth, props.dropdownStyle?.containerJustifyContent, props.containerClickable, props.dropdownStyle?.labelUp, props.dropdownStyle?.margin, props.dropdownStyle?.marginRight, props.dropdownStyle?.alignSelf)} >
			{
				props.label &&
				<LabelContainer alignSelf={props.dropdownStyle?.labelUp ? 'start' : 'center'}>
					{
						props.required &&
						<Asterisk />
					}
					<Label {...props.dropdownStyle?.labelStyle}>{props.label}</Label>
				</LabelContainer>
			}
			<DropdownContainer disabled={props.disabled} cursor={props.dropdownStyle?.cursor} onClick={(event) => {
				if (props.stopPropagation) event.stopPropagation();
				if (!isOpen && props.autoOptionUp) {
					setOptionUp(event.clientY >= (window.innerHeight / 2.0));
				}
				if (isOpen && !(props.checkbox || props.isMulti) || !isOpen && !props.controlledJSXButtonOpen && !props.disabled) {
					setOpen(!isOpen);
				}
			}}>
				{(props.JSXButton == null || props.JSXButtonInsideContainer) && (
					<InputContainer border={props.dropdownStyle?.containerBorder} width={width} height={height} backgroundColor={props.dropdownStyle?.backgroundColor} >
						{
							props.image
								? <OptionImage src={props.image} alt='' width={props.dropdownStyle?.imageWidth ?? '20px'} height={props.dropdownStyle?.imageHeight} />
								: props.imageJsx
									? <OptionImageContainer width={props.dropdownStyle?.imageWidth ?? '20px'} height={props.dropdownStyle?.imageHeight}>{props.imageJsx}</OptionImageContainer>
									: props.isMulti
										? undefined
										: value?.image
											? <OptionImage src={value.image} alt={typeof value.label === 'string' ? value.label : undefined} />
											: ((value?.imageJsx) != null)
												? <OptionImageJsx>{value?.imageJsx}</OptionImageJsx>
												: <></>
						}
						{!props.isMulti && !props.JSXButtonInsideContainer &&
							<InputBlock
								margin={props.dropdownStyle?.inputMargin}
								padding={props.dropdownStyle?.inputPadding}
								clickable={props.onClick !== undefined}
								id={props.id}
								name={props.name}
								fontSize={props.dropdownStyle?.fontSize}
								fontColor={props.dropdownStyle?.fontColor}
								fontWeight={props.dropdownStyle?.fontWeight}
								type="text"
								onClick={props.onClick}
								onChange={(e) => {
									setOpen(true);
									setLabel(e.target.value);
									setList(props.datalist.filter(data => typeof data.label === 'string' ? data.label.toLocaleLowerCase().includes(e.target.value.toLocaleLowerCase()) : true));
									// ValueChange(value, e.target.value, props, setValue)
								}} value={typeof label === 'string' ? label : ''} backgroundColor={value?.color ?? props.dropdownStyle?.backgroundColor} required={props.required} readOnly={props.searchable ? true : props.readOnly} disabled={props.disabled} placeholder={props.placeholder} />
						}
						{props.isMulti && !props.JSXButtonInsideContainer && <MultiLabelContainerWrapper>{list.filter(e => e.checked).map(e => <MultiLabelContainer key={`${e.value}`}>
							{e.image && <OptionImage src={e.image} alt={typeof e.label === 'string' ? e.label : undefined} />}
							{e.imageJsx && <OptionImageJsx>{e?.imageJsx}</OptionImageJsx>}
							{e.label}
						</MultiLabelContainer>)}</MultiLabelContainerWrapper>}
						{props.JSXButtonInsideContainer && props.JSXButton && props.JSXButton({ isOpen, setIsOpen: setOpen, width, height, value })}

						{label && props.cancellable && (
							<CleanButton
								className="btn btn-transparent p-0"
								type="button"
								onClick={e => {
									e.stopPropagation();
									setLabel('');
									setValue(undefined);
									props.onChange?.({ label: '', value: null } as OnChangeType);
									setOpen(false);
								}}
							>
								<i className="fas fa-times" />
							</CleanButton>
						)}
						{!props.dropdownStyle?.noArrow && <ArrowBlock
							className={props.dropdownStyle?.arrowClassName}
							arrowSize={props.dropdownStyle?.arrowSize}
							url={props.dropdownStyle?.arrowClassName ? ' ' : props.dropdownStyle?.arrowImage}
							color={props.dropdownStyle?.arrowColor}
							fontSize={props.dropdownStyle?.arrowFontSize}
						/>}

					</InputContainer>
				)}
				{(props.JSXButton != null && !props.JSXButtonInsideContainer) && (
					<FlexDiv cursor={props.disabled ? 'not-allowed' : 'pointer'} height='100%' width='100%' justify={props.dropdownStyle?.jsxButtonContainerJustify ?? 'center'}
						onClick={() => !props.controlledJSXButtonOpen && !props.disabled && setOpen(!isOpen)}>
						{props.JSXButton({ isOpen, setIsOpen: setOpen, width, height, value })}
					</FlexDiv>
				)}
				{(props.allwaysOpen || isOpen) && props.JSXOption && <OptionContainer isClose={!isOpen} top={isOptionUp ? undefined : props.dropdownStyle?.containerTop ?? height} bottom={isOptionUp ? '100%' : undefined} height={optionHeight} minHeight={props.dropdownStyle?.optionMinHeight} width={optionWidth} left={props.dropdownStyle?.optionLeft}>
					<InputSearch name={''} type='text' onClick={e => e.stopPropagation()} onChange={setSearch} value={search} noBorderRadius />
					{props.JSXOption({ options: list.filter(v => typeof v.label == 'string' ? v.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()) : true), onOptionClicked, setIsOpen: setOpen })}
				</OptionContainer>
				}
				{(props.allwaysOpen || isOpen) && !props.JSXOption &&
					<OptionContainer isClose={!isOpen} top={isOptionUp ? undefined : props.dropdownStyle?.containerTop ?? height} bottom={isOptionUp ? '100%' : undefined} height={optionHeight} minHeight={props.dropdownStyle?.optionMinHeight} width={optionWidth} left={props.dropdownStyle?.optionLeft}>
						{props.before}
						{searchable && <InputSearch name={''} type='text' onClick={e => e.stopPropagation()} onChange={setSearch} value={search} noBorderRadius />}
						{
							searchable && props.checkbox && props.checkboxAll &&
							<div
								style={{ ...OptionBlockClickable(), height: optionBlockHeight, display: 'flex', alignItems: 'center', width: '100%' }}
								key={`option[${props.name}][all]`}
								onClick={() => {
									const checked = list.every(o => o.checked);
									const nList = list.map(o => ({
										...o,
										checked: !checked
									}));
									setList(nList);
									if (!props.validateButton) props.onChange?.(nList.filter(o => o.checked) as OnChangeType);
								}}
							>
								<OptionBlock
									borderBottom={`${BorderColor} 1px solid`}
									heightLength={optionBlockHeight}
									padding={props.dropdownStyle?.optionPadding}
									width='100%'
								>
									<Checkbox isActive={list.every(o => o.checked)} />
									<OptionText size={props.dropdownStyle?.optionFontSize}><Translate id='all' /></OptionText>
								</OptionBlock>
							</div>
						}
						<div style={{ width: '100%', height: '100%', ...(props.gallery ? { display: 'flex', flexWrap: 'wrap' } : {}) }}>
							{(props.isMulti && !props.doNotDeleteOptionOnSelect ? list.filter(o => !o.checked) : list).length == 0 &&
								<FlexDiv height={'30px'} justify="center"><DefaultTextDiv><Translate id='no_option' /></DefaultTextDiv></FlexDiv>
							}
							{
								list.map((option, optionI) => {
									if (!(typeof option.label == 'string' ? option.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()) : true)) return <></>;
									if (option.checked && props.isMulti && !props.doNotDeleteOptionOnSelect) return <></>;
									return (
										<div
											style={props.gallery ? { cursor: 'pointer' } : OptionBlockClickable()}
											key={`option[${props.name}][${optionI}]`}
											onClick={e => {
												if (option.disabled) e.stopPropagation();
												if (!option.disabled)
													onOptionClicked(option, optionI);
											}}
										>
											{(props.JSXContent != null)
												? props.JSXContent(option)
												: (props.JSXContentAndButton != null)
													? props.JSXContentAndButton(isOpen, setOpen, option)
													:
													<OptionBlock
														disabled={option.disabled}
														heightLength={optionBlockHeight}
														backgroundColor={option.color ?? props.dropdownStyle?.backgroundColor}
														padding={props.dropdownStyle?.optionPadding}
														width={props.dropdownStyle?.optionTextWidth}
														title={option.disabledReason ?? undefined}
													>
														{
															props.checkbox && <Checkbox isActive={option.checked ?? false} />
														}
														{
															option.image &&
															<OptionImage src={option.image} alt='' onError={_ => setList(list.map(v => {
																if (JSON.stringify(v.value) === JSON.stringify(option.value)) {
																	v.image = undefined;
																	return v;
																}
																return v;
															}))} />
														}
														{
															option.imageJsx && <OptionImageJsx>{option.imageJsx}</OptionImageJsx>
														}
														<OptionText color={props.dropdownStyle?.optionFontColor} size={props.dropdownStyle?.optionFontSize} weight={props.dropdownStyle?.optionFontWeight} ellipsis={props.dropdownStyle?.optionEllipsis}>{option.label}</OptionText>
													</OptionBlock>
											}
										</div>
									);
								})
							}
						</div>
						{
							props.validateButton &&
							<ValidateContainer>
								<ValidateButton onClick={() => {
									props.onChange?.(list.filter(l => l.checked) as OnChangeType);
									setOpen(false);
								}}><Translate id='validate' /></ValidateButton>
							</ValidateContainer>
						}
					</OptionContainer>
				}
			</DropdownContainer>
		</div>)
	);
}

export default Dropdown;
